import React, { useEffect, useState } from "react";
import { FileSignature, Receipt, Download, ShieldCheck, Home, User, CheckCircle2, Clock, CreditCard, Loader2, AlertTriangle, X, Wrench, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

export default function MonEspace() {
  const [data, setData] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [showIncident, setShowIncident] = useState(false);

  const load = () => api.get("/tenant/space").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const payRent = async (r) => {
    setPayingId(r.id);
    try {
      const res = await api.post(`/receipts/${r.id}/pay`, { origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de lancer le paiement.");
      setPayingId(null);
    }
  };

  if (!data) return <div className="py-20 text-center text-brand-green/50">Chargement...</div>;

  const p = data.profile || {};
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 border border-brand-soft flex items-center gap-4" data-testid="tenant-profile">
        <span className="w-14 h-14 rounded-2xl bg-brand-lime flex items-center justify-center font-semibold text-xl text-brand-greenDeep">{(p.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</span>
        <div className="flex-1">
          <h2 className="font-display text-xl font-semibold text-brand-greenDeep">{p.name || "Mon espace"}</h2>
          <p className="text-sm text-brand-green/50 flex items-center gap-3 flex-wrap">
            {p.email && <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {p.email}</span>}
            {p.property && <span className="flex items-center gap-1"><Home className="w-3.5 h-3.5" /> {p.property}</span>}
          </p>
        </div>
        <button onClick={() => setShowIncident(true)} data-testid="report-incident-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-greenDeep text-brand-lime font-semibold text-sm hover:opacity-90 transition-opacity">
          <AlertTriangle className="w-4 h-4" /> Signaler un incident
        </button>
        {p.status && <span className={`text-xs px-3 py-1 rounded-full font-semibold ${p.status === "À jour" ? "bg-brand-lime/30 text-brand-greenDeep" : "bg-red-100 text-red-600"}`}>{p.status}</span>}
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-brand-soft"><div className="flex items-center gap-2 text-brand-green/60 text-sm mb-1"><CheckCircle2 className="w-4 h-4" /> Loyers réglés</div><p className="text-2xl font-bold text-brand-greenDeep">{data.paid.toLocaleString("fr-FR")} €</p></div>
        <div className="bg-white rounded-2xl p-6 border border-brand-soft"><div className="flex items-center gap-2 text-brand-green/60 text-sm mb-1"><Clock className="w-4 h-4" /> En attente</div><p className="text-2xl font-bold text-amber-600">{data.pending.toLocaleString("fr-FR")} €</p></div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-brand-soft">
        <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><FileSignature className="w-5 h-5" /> Mes baux signés</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {data.leases.map((l) => (
            <div key={l.id} className="flex items-start gap-3 p-3 rounded-xl border border-brand-soft" data-testid={`tenant-lease-${l.id}`}>
              <span className="w-10 h-10 rounded-lg bg-brand-lime/30 flex items-center justify-center text-brand-greenDeep shrink-0"><FileSignature className="w-5 h-5" /></span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-brand-greenDeep truncate">Bail — {l.property_name}</p>
                <p className="text-xs text-brand-green/50">Bailleur : {l.owner_name} · Signé {l.signed_at}</p>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <a href={imgSrc(l.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-brand-softer text-brand-greenDeep font-semibold hover:bg-brand-soft transition-colors" data-testid={`tenant-lease-download-${l.id}`}><Download className="w-3 h-3" /> Télécharger</a>
                  {l.sealed && (
                    <a href={`/verifier/${l.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-brand-lime/30 text-brand-greenDeep font-semibold hover:bg-brand-lime/50 transition-colors" data-testid={`tenant-lease-verify-${l.id}`}><ShieldCheck className="w-3 h-3" /> Vérifier l'horodatage</a>
                  )}
                </div>
              </div>
            </div>
          ))}
          {data.leases.length === 0 && <p className="text-sm text-brand-green/50 col-span-full">Aucun bail signé pour le moment.</p>}
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-brand-soft">
        <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Receipt className="w-5 h-5" /> Mes quittances</h3>
        <div className="space-y-2">
          {data.receipts.map((r) => (
            <div key={r.id} className="flex items-center justify-between py-2.5 border-b border-brand-soft last:border-0" data-testid={`tenant-receipt-${r.id}`}>
              <div>
                <p className="text-sm font-medium text-brand-greenDeep">{r.month}</p>
                <p className="text-xs text-brand-green/50">{r.property}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold text-brand-greenDeep">{(r.amount || 0).toLocaleString("fr-FR")} €</span>
                {r.status === "Payé" ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-brand-lime/30 text-brand-greenDeep">Payé</span>
                ) : (
                  <button onClick={() => payRent(r)} disabled={payingId === r.id} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold transition-colors disabled:opacity-60" data-testid={`pay-rent-btn-${r.id}`}>
                    {payingId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />} Régler
                  </button>
                )}
              </div>
            </div>
          ))}
          {data.receipts.length === 0 && <p className="text-sm text-brand-green/50">Aucune quittance disponible.</p>}
        </div>
      </div>

      {showIncident && <IncidentModal property={p.property} onClose={() => setShowIncident(false)} />}
    </div>
  );
}

function IncidentModal({ property, onClose }) {
  const [artisans, setArtisans] = useState([]);
  const [domain, setDomain] = useState("Tous");
  const [artisanId, setArtisanId] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [diagBusy, setDiagBusy] = useState(false);
  const [diagImg, setDiagImg] = useState("");
  const [diag, setDiag] = useState(null);

  const runDiagnostic = async () => {
    if (!description.trim() && !diagImg) return toast.error("Décrivez le problème ou ajoutez une photo.");
    setDiagBusy(true);
    try {
      const res = await api.post("/ai/diagnose-work", { description: description.trim(), image_base64: diagImg });
      setDiag(res.data);
      if (res.data.category) { setCategory(res.data.category); setDomain(res.data.category); }
      if (res.data.suggested_artisans?.length) setArtisanId(res.data.suggested_artisans[0].id);
      toast.success("Diagnostic IA effectué");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnostic indisponible");
    } finally { setDiagBusy(false); }
  };

  useEffect(() => {
    api.get("/artisans/directory").then((r) => setArtisans(r.data || [])).catch(() => {});
  }, []);

  const domains = ["Tous", ...Array.from(new Set(artisans.map((a) => a.domain).filter(Boolean)))];
  const filtered = domain === "Tous" ? artisans : artisans.filter((a) => a.domain === domain);

  const submit = async () => {
    if (!artisanId) return toast.error("Choisissez un artisan.");
    if (!description.trim()) return toast.error("Décrivez le problème.");
    setSubmitting(true);
    try {
      const res = await api.post("/tenant/incident", { artisan_id: artisanId, category, description: description.trim(), diagnostic: diag || null, photo_base64: diagImg || "" });
      toast.success(`Incident transmis à ${res.data.artisan_name}. Suivez-le dans « Mes travaux ».`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-3xl border border-brand-soft shadow-xl p-6" onClick={(e) => e.stopPropagation()} data-testid="incident-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Signaler un incident</h3>
          <button onClick={onClose} className="text-brand-green/50 hover:text-brand-greenDeep"><X className="w-5 h-5" /></button>
        </div>
        {property && <p className="text-xs text-brand-green/50 mb-4 flex items-center gap-1"><Home className="w-3.5 h-3.5" /> {property}</p>}

        <label className="block text-sm font-medium text-brand-green/70 mb-1.5">Type de problème</label>
        <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Fuite d'eau, panne électrique..." className="w-full px-4 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-brand-limeDark mb-4" data-testid="incident-category-input" />

        <label className="block text-sm font-medium text-brand-green/70 mb-1.5">Spécialité</label>
        <select value={domain} onChange={(e) => { setDomain(e.target.value); setArtisanId(""); }} className="w-full px-4 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-brand-limeDark mb-4 bg-white" data-testid="incident-domain-select">
          {domains.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>

        <label className="block text-sm font-medium text-brand-green/70 mb-1.5 flex items-center gap-1"><Wrench className="w-3.5 h-3.5" /> Artisan</label>
        {filtered.length === 0 ? (
          <p className="text-sm text-brand-green/50 mb-4">Aucun artisan disponible pour cette spécialité.</p>
        ) : (
          <select value={artisanId} onChange={(e) => setArtisanId(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-brand-limeDark mb-4 bg-white" data-testid="incident-artisan-select">
            <option value="">— Choisir un artisan —</option>
            {filtered.map((a) => <option key={a.id} value={a.id}>{`${a.company} · ${a.city}${a.avg_rating ? ` · ${a.avg_rating}★` : ""}`}</option>)}
          </select>
        )}

        <label className="block text-sm font-medium text-brand-green/70 mb-1.5">Description</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="Décrivez le problème rencontré..." className="w-full px-4 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-brand-limeDark mb-3 resize-none" data-testid="incident-description-input" />

        <div className="mb-4 rounded-xl border border-brand-soft bg-brand-softer/50 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-brand-soft cursor-pointer text-xs text-brand-green/60 hover:border-brand-lime bg-white">
              <Upload className="w-3.5 h-3.5" /> {diagImg ? "Photo ajoutée ✓" : "Ajouter une photo"}
              <input type="file" accept="image/*" className="hidden" data-testid="incident-photo-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) { const r = new FileReader(); r.onload = () => setDiagImg(String(r.result).split(",")[1]); r.readAsDataURL(f); } }} />
            </label>
            <button type="button" onClick={runDiagnostic} disabled={diagBusy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-xs font-semibold disabled:opacity-60" data-testid="incident-diagnose-btn">
              {diagBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Diagnostic IA
            </button>
          </div>
          {diag && (
            <div className="mt-3 text-sm" data-testid="incident-diag-result">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-brand-lime/30 text-brand-greenDeep">{diag.category}</span>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${diag.urgency === "Critique" ? "bg-red-100 text-red-700" : diag.urgency === "Élevée" ? "bg-orange-100 text-orange-700" : diag.urgency === "Moyenne" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>Urgence : {diag.urgency}</span>
              </div>
              {diag.summary && <p className="text-brand-green/70 mt-2 text-xs leading-relaxed">{diag.summary}</p>}
              {diag.advice && <p className="text-brand-green/55 mt-1 text-xs leading-relaxed">💡 {diag.advice}</p>}
              <p className="text-[11px] text-brand-green/45 mt-2">La spécialité et l'artisan ont été pré-sélectionnés selon le diagnostic.</p>
            </div>
          )}
        </div>

        <button onClick={submit} disabled={submitting} className="w-full py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors" data-testid="incident-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />} Envoyer la demande
        </button>
      </div>
    </div>
  );
}
